/**
 * What a blob can look like. Shared by the client (maker + renderer) and the
 * server (validation), so a look the server accepts is always one the client
 * can draw.
 *
 * Bodies come from blobatar: a face seed (eye spacing, size and the like) plus
 * pinned shape, hue and tone. Values below are the midpoints of blobatar@2's
 * frozen bands, so they are stable for the whole major.
 */

export const SHAPES = [
  { id: 'round', label: 'Round', trait: 0.11 },
  { id: 'organic', label: 'Pebble', trait: 0.35 },
  { id: 'boxy', label: 'Boxy', trait: 0.54 },
  { id: 'capsule', label: 'Capsule', trait: 0.65 },
  { id: 'nub', label: 'Nub', trait: 0.745 },
  { id: 'cloud', label: 'Cloud', trait: 0.825 },
  { id: 'droplet', label: 'Droplet', trait: 0.8875 },
  { id: 'hexagon', label: 'Hexagon', trait: 0.9325 },
  { id: 'sun', label: 'Sunny', trait: 0.965 },
  { id: 'triangle', label: 'Pointy', trait: 0.99 },
] as const

export const TONES = [
  { id: 'pastel', label: 'Pastel', value: 0.1 },
  { id: 'pale', label: 'Pale', value: 0.28 },
  { id: 'mid', label: 'Mid', value: 0.49 },
  { id: 'deep', label: 'Deep', value: 0.71 },
  { id: 'bright', label: 'Bright', value: 0.865 },
  { id: 'ink', label: 'Ink', value: 0.965 },
] as const

/** Hue wheel in 15° steps. */
export const HUE_STEP = 15
export const HUE_COUNT = 360 / HUE_STEP

export const HATS = [
  { id: 'none', label: 'No hat' },
  { id: 'party', label: 'Party hat' },
  { id: 'tophat', label: 'Top hat' },
  { id: 'beanie', label: 'Beanie' },
  { id: 'crown', label: 'Crown' },
  { id: 'cowboy', label: 'Cowboy' },
  { id: 'chef', label: 'Chef' },
  { id: 'flower', label: 'Flower' },
  { id: 'propeller', label: 'Propeller' },
  { id: 'wizard', label: 'Wizard' },
  { id: 'bunny', label: 'Bunny ears' },
  { id: 'cat', label: 'Cat ears' },
  { id: 'halo', label: 'Halo' },
  { id: 'frog', label: 'Frog' },
  { id: 'sprout', label: 'Sprout' },
  { id: 'bow', label: 'Bow' },
  { id: 'viking', label: 'Viking' },
  { id: 'headphones', label: 'Headphones' },
  { id: 'beret', label: 'Beret' },
  { id: 'cap', label: 'Cap' },
  { id: 'mushroom', label: 'Mushroom' },
  { id: 'snorkel', label: 'Goggles' },
] as const

export type HatId = (typeof HATS)[number]['id']
const HAT_IDS = new Set<string>(HATS.map((h) => h.id))

export interface Look {
  /** Face seed: picks the eyes. 4–12 chars of [a-z0-9]. */
  seed: string
  shape: number
  /** Hue index, 0..HUE_COUNT-1. */
  hue: number
  tone: number
  hat: HatId
}

const SEED = /^[a-z0-9]{4,12}$/

/** Strict: returns a clean copy or null. Never throws. */
export function parseLook(v: unknown): Look | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const int = (x: unknown, max: number) => (Number.isInteger(x) && (x as number) >= 0 && (x as number) < max ? (x as number) : null)
  const shape = int(o.shape, SHAPES.length)
  const hue = int(o.hue, HUE_COUNT)
  const tone = int(o.tone, TONES.length)
  if (shape === null || hue === null || tone === null) return null
  if (typeof o.seed !== 'string' || !SEED.test(o.seed)) return null
  if (typeof o.hat !== 'string' || !HAT_IDS.has(o.hat)) return null
  return { seed: o.seed, shape, hue, tone, hat: o.hat as HatId }
}

/** A random look, using whatever randomness the caller has. */
export function randomLook(rand: () => number = Math.random): Look {
  const pick = <T>(xs: readonly T[]) => xs[Math.floor(rand() * xs.length)]!
  let seed = ''
  for (let i = 0; i < 8; i++) seed += 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(rand() * 36)]
  // Lean towards the friendly tones; ink is a rare find, like blobatar intends.
  const tone = pick([0, 0, 1, 2, 2, 3, 4, 4, 5])
  const hats = HATS.filter((h) => h.id !== 'none')
  return { seed, shape: Math.floor(rand() * SHAPES.length), hue: Math.floor(rand() * HUE_COUNT), tone, hat: rand() < 0.75 ? pick(hats).id : 'none' }
}
